import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { fetchUsageLog } from "../../services/adminService";
import { TokenUsageLogs } from "../../models/usageLog";

const UsageLog: React.FC = () => {
  const [usageLog, setUsageLog] = useState<TokenUsageLogs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [responseContent, setResponseContent] = useState<string>("");
  const [nextPageToken, setNextPageToken] = useState<string | null>("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [toastOpen, setToastOpen] = useState<boolean>(false);

  const lastRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    fetchUsageLog('')
      .then((data) => {
        setUsageLog(data);
        setNextPageToken(data.nextPageToken || '');
      })
      .catch((err) => {
        console.error('Failed to fetch initial usage log:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getUsageLog = useCallback(async (initialLoad: boolean = false) => {
    if (loading || (usageLog?.logs?.length && !initialLoad)) return;

    try {
      if (initialLoad) {
        setLoading(true);
      }

      const data = await fetchUsageLog(nextPageToken || "");

      setUsageLog((prev) => ({
        logs: prev?.logs ? [...prev.logs, ...data.logs] : data.logs,
        nextPageToken: data.nextPageToken || "",
      }));
      setNextPageToken(data.nextPageToken || "");
      setHasNextPage(!!data.nextPageToken);
    } catch (err: any) {
      setError(err.message || "로그 데이터를 불러오는데 실패했습니다");
    } finally {
      if (initialLoad) {
        setLoading(false);
      }
    }
  }, [nextPageToken, usageLog?.logs?.length, loading]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          getUsageLog();
        }
      },
      { root: null, rootMargin: "100px", threshold: 0.1 }
    );

    if (lastRowRef.current) {
      observer.observe(lastRowRef.current);
    }

    return () => {
      if (lastRowRef.current) {
        observer.unobserve(lastRowRef.current);
      }
    };
  }, [getUsageLog, hasNextPage]);

  useEffect(() => {
    if (!usageLog?.logs?.length) {
      getUsageLog(true);
    }
  }, [getUsageLog, usageLog?.logs?.length]);

  const handleOpenModal = (response: string) => {
    setResponseContent(response);
    setOpenModal(true);
  };

  const handleCopyResponse = () => {
    navigator.clipboard.writeText(responseContent);
    setToastOpen(true);
  };

  const formatJsonResponse = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleRefresh = () => {
    setUsageLog(null);
    setNextPageToken("");
    setLoading(true);
    fetchUsageLog("")
      .then((data) => {
        setUsageLog(data);
        setNextPageToken(data.nextPageToken || "");
      })
      .catch((err) => {
        console.error('Failed to fetch usage log:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading && !usageLog?.logs?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          사용 기록
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ ml: 'auto' }}
        >
          새로고침
        </Button>
      </Box>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 4,
          borderRadius: 2,
          '& .MuiAlert-message': {
            fontWeight: 'medium'
          }
        }}
      >
        정상 응답 케이스에서만 토큰이 소진됩니다.
      </Alert>

      <Card elevation={0} sx={{ bgcolor: 'white', borderRadius: 2 }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>시간</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>경로</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="center">연산시간</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="center">상태</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="center">사용 토큰</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="center">남은 토큰</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="center">응답 내용</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usageLog?.logs?.map((log, index) => (
                <TableRow
                  key={index}
                  ref={index === usageLog.logs.length - 1 ? lastRowRef : null}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(log.timestamp), 'yyyy.MM.dd HH:mm:ss.SSS', { locale: ko })}
                  </TableCell>
                  <TableCell>{log.jsonPayload.path}</TableCell>
                  <TableCell align="center">
                    {log.jsonPayload.computationTime}ms
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={log.jsonPayload.success ? "성공" : "실패"}>
                      {log.jsonPayload.success ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {log.jsonPayload.usedTokens} 토큰
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      color: log.jsonPayload.remainingToken < 1000 ? 'error.main' : 'success.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {log.jsonPayload.remainingToken} 토큰
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="응답 내용 보기">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(log.jsonPayload.response)}
                        color="primary"
                      >
                        <ArticleIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography>응답 상세 내용</Typography>
          <Tooltip title="복사하기">
            <IconButton onClick={handleCopyResponse} size="small">
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography
            variant="body1"
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontSize: '0.875rem'
            }}
          >
            {formatJsonResponse(responseContent)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => setOpenModal(false)}
            variant="contained"
            color="primary"
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message="클립보드에 복사되었습니다."
      />
    </Box>
  );
};

export default UsageLog;